import { PhotoCrosshair } from 'features/kestrelViewer/ui/tools/photoreel/components/PhotoCrosshair'
import { usePhotoUrl } from 'features/kestrelViewer/ui/tools/photoreel/hooks/usePhotoUrl'
import React from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

import { PhotoReelDropdown } from './PhotoReelDropdown'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8); /* Darken background */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* Ensure it covers other modals and popups */
`

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  padding: 1rem;
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 20;
`

const FullImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform 0.1s ease-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #87ceeb;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
type ImageType = 'original' | 'sam2' | 'siglip'

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  gap: 8px;
`

const ErrorText = styled.div`
  font-size: 14px;
  opacity: 0.6;
`

interface PhotoReelModalProps {
  photo: KVPhoto
  onClose: () => void
}

const IMAGE_TYPE_CONFIG = {
  original: { urlType: 'fullSize' as const, alt: 'Full resolution photo' },
  sam2: { urlType: 'sam2' as const, alt: 'SAM2 processed photo' },
  siglip: { urlType: 'siglip' as const, alt: 'SIGLIP processed photo' },
} as const

export const PhotoReelModal: React.FC<PhotoReelModalProps> = ({
  photo,
  onClose,
}) => {
  // Viewport state
  const [viewport, setViewport] = React.useState({
    zoom: 1,
    pan: { x: 0, y: 0 },
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    hasMoved: false,
  })

  const [currentImageType, setCurrentImageType] =
    React.useState<ImageType>('original')
  const [imageDimensions, setImageDimensions] = React.useState<{
    width: number
    height: number
  } | null>(null)

  const {
    url: currentImageUrl,
    isLoading: urlLoading,
    error: urlError,
  } = usePhotoUrl(photo, IMAGE_TYPE_CONFIG[currentImageType].urlType)

  const isImageLoading = urlLoading || !currentImageUrl
  const currentImageAlt = IMAGE_TYPE_CONFIG[currentImageType].alt

  const availableImageTypes: ImageType[] = ['original', 'sam2', 'siglip']
  const canShowDropdown = availableImageTypes.length > 1

  // Calculate container dimensions
  const getContainerDimensions = React.useCallback(() => {
    if (!imageDimensions) return { width: 'auto', height: 'auto' }

    const maxWidth = window.innerWidth * 0.88 - 32
    const maxHeight = window.innerHeight * 0.88 - 64
    const imageAspectRatio = imageDimensions.width / imageDimensions.height
    const maxAspectRatio = maxWidth / maxHeight

    const [containerWidth, containerHeight] =
      imageAspectRatio > maxAspectRatio
        ? [maxWidth, maxWidth / imageAspectRatio]
        : [maxHeight * imageAspectRatio, maxHeight]

    return {
      width: `${containerWidth}px`,
      height: `${containerHeight}px`,
    }
  }, [imageDimensions])

  // Event handlers
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !viewport.hasMoved) {
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
  }

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget

    if (!imageDimensions) {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.95 : 1.05

    setViewport((prev) => {
      const newZoom = Math.min(Math.max(prev.zoom * delta, 1), 5)
      const zoomRatio = newZoom / prev.zoom
      const scaledPan = { x: prev.pan.x * zoomRatio, y: prev.pan.y * zoomRatio }

      const containerDims = getContainerDimensions()
      const maxPanX =
        ((newZoom - 1) * (parseInt(containerDims.width) || 200)) / 2
      const maxPanY =
        ((newZoom - 1) * (parseInt(containerDims.height) || 200)) / 2

      return {
        ...prev,
        zoom: newZoom,
        pan: {
          x:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.x, -maxPanX), maxPanX),
          y:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.y, -maxPanY), maxPanY),
        },
      }
    })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewport.zoom <= 1) return
    setViewport((prev) => ({
      ...prev,
      isDragging: true,
      hasMoved: false,
      dragStart: { x: e.clientX - prev.pan.x, y: e.clientY - prev.pan.y },
    }))
  }

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleImageTypeChange = (type: ImageType) => {
    setCurrentImageType(type)
    setImageDimensions(null)
  }

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const projection = photo.projection

  // Calculate crosshair position accounting for zoom and pan
  const getCrosshairPosition = () => {
    if (!projection || !imageDimensions) return null

    const containerDimensions = getContainerDimensions()
    const containerWidth = parseInt(containerDimensions.width as string) || 0
    const containerHeight = parseInt(containerDimensions.height as string) || 0

    // Calculate how the image is scaled to fit within the container
    const containerAspectRatio = containerWidth / containerHeight
    const imageAspectRatio = imageDimensions.width / imageDimensions.height

    let scaledImageWidth: number
    let scaledImageHeight: number
    let offsetX: number
    let offsetY: number

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container aspect ratio - constrained by width
      scaledImageWidth = containerWidth
      scaledImageHeight = containerWidth / imageAspectRatio
      offsetX = 0
      offsetY = (containerHeight - scaledImageHeight) / 2
    } else {
      // Image is taller than container aspect ratio - constrained by height
      scaledImageHeight = containerHeight
      scaledImageWidth = containerHeight * imageAspectRatio
      offsetX = (containerWidth - scaledImageWidth) / 2
      offsetY = 0
    }

    // Calculate the base position on the scaled image (before zoom/pan)
    // Always use original image dimensions for crosshair positioning since projection
    // coordinates are always relative to the original image, not the processed variants
    const imageRelativeX = projection.x / projection.originalImageWidth
    const imageRelativeY = projection.y / projection.originalImageHeight

    // Position within the scaled image area
    const baseX = offsetX + imageRelativeX * scaledImageWidth
    const baseY = offsetY + imageRelativeY * scaledImageHeight

    // Calculate center point of the container for transform origin
    const centerX = containerWidth / 2
    const centerY = containerHeight / 2

    // Apply the same transform as the image: translate then scale from center
    // The CSS transform is: translate(pan.x, pan.y) scale(zoom) with origin center
    // So we first apply scale from center, then translate
    const scaledX = centerX + (baseX - centerX) * viewport.zoom
    const scaledY = centerY + (baseY - centerY) * viewport.zoom

    // Then apply pan
    const finalX = scaledX + viewport.pan.x
    const finalY = scaledY + viewport.pan.y

    return { x: finalX, y: finalY }
  }

  const crosshairPosition = getCrosshairPosition()

  // Handle global mouse events during dragging
  React.useEffect(() => {
    if (!viewport.isDragging) return

    const handleGlobalMouseMove = (e: MouseEvent) => {
      setViewport((prev) => {
        const newPan = {
          x: e.clientX - prev.dragStart.x,
          y: e.clientY - prev.dragStart.y,
        }

        const containerDims = getContainerDimensions()
        const maxPanX =
          ((prev.zoom - 1) * (parseInt(containerDims.width) || 200)) / 2
        const maxPanY =
          ((prev.zoom - 1) * (parseInt(containerDims.height) || 200)) / 2

        return {
          ...prev,
          hasMoved: true,
          pan: {
            x: Math.min(Math.max(newPan.x, -maxPanX), maxPanX),
            y: Math.min(Math.max(newPan.y, -maxPanY), maxPanY),
          },
        }
      })
    }

    const handleGlobalMouseUp = () => {
      setViewport((prev) => ({ ...prev, isDragging: false }))
      setTimeout(() => {
        setViewport((prev) => ({ ...prev, hasMoved: false }))
      }, 100)
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [
    viewport.isDragging,
    viewport.dragStart,
    viewport.zoom,
    getContainerDimensions,
  ])

  return (
    <ModalOverlay
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}>
      <ModalContent>
        {canShowDropdown && !isImageLoading && (
          <DropdownContainer>
            <PhotoReelDropdown
              currentImageType={currentImageType}
              availableImageTypes={availableImageTypes}
              onImageTypeChange={handleImageTypeChange}
              disabled={isImageLoading}
            />
          </DropdownContainer>
        )}

        <ImageContainer style={getContainerDimensions()}>
          {urlError ? (
            <ErrorContainer>
              <div>Failed to load {currentImageAlt.toLowerCase()}</div>
              <ErrorText>{urlError}</ErrorText>
            </ErrorContainer>
          ) : (
            <>
              {isImageLoading && <LoadingSpinner />}

              {currentImageUrl && (
                <FullImage
                  src={currentImageUrl}
                  alt={currentImageAlt}
                  draggable={false}
                  onLoad={handleImageLoad}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onDragStart={handleDragStart}
                  style={{
                    display: isImageLoading ? 'none' : 'block',
                    transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom})`,
                    transformOrigin: 'center center',
                    cursor:
                      viewport.zoom > 1
                        ? viewport.isDragging
                          ? 'grabbing'
                          : 'grab'
                        : 'default',
                  }}
                />
              )}

              {crosshairPosition && !isImageLoading && (
                <PhotoCrosshair
                  x={crosshairPosition.x}
                  y={crosshairPosition.y}
                  scale={1.0}
                  originalImageWidth={projection?.originalImageWidth}
                  originalImageHeight={projection?.originalImageHeight}
                />
              )}
            </>
          )}
        </ImageContainer>
      </ModalContent>
    </ModalOverlay>
  )
}
